import type { ArticleBlock, ArticleJson, ArticleSection } from "../types/SemanticTypes";
import { wordService } from "../services/wordService";

// Body headings/paragraphs whose entire text is one of these labels are always
// frontmatter duplicates, never real article content — the renderer draws its
// own "ABSTRACT"/"Keywords"/"References" labels from the typed frontmatter
// controls (and the abstract/keywords panel). Auto-tag doesn't always tag the
// heading paragraph consistently (section vs paragraph, with or without the
// "Libertatem <Label> Heading" control title), so match by text as the reliable
// last line of defense — see isReservedBodyLabel below.
const RESERVED_FRONTMATTER_HEADINGS = new Set(["references"]);
const RESERVED_BODY_LABELS = new Set([
  "abstract", "keywords", "keyword", "key words", "key word", "index terms",
  "references", "bibliography", "works cited", "endnotes", "endnote"
]);
// True when the text is *only* a reserved label (after stripping a trailing
// colon/period and collapsing whitespace) — e.g. "Abstract", "Keywords:".
// A real body paragraph that merely starts with "Abstract" (e.g. "Abstract
// reasoning is …") has more text and is not matched.
function isReservedBodyLabel(text: string): boolean {
  const s = text.trim().toLowerCase().replace(/[:.\s]+$/, "").replace(/\s+/g, " ");
  return RESERVED_BODY_LABELS.has(s);
}

// Restore footnote/citation numbers as superscripts. Works on both plain and
// rich text. When richText omits numbers that plainText has (Office.js strips
// w:footnoteReference from body.getOoxml()), splice them back from plain.
function restoreFootnoteRefs(richText: string | undefined, plainText: string): string {
  const text = richText ?? plainText;
  if (!text) return "";
  // Find .N patterns in plain text — footnote refs after sentence-ending punctuation.
  // Negative lookbehind: digit before punctuation means it's a number (1,00,000), not a ref.
  const FN_RE = /(?<!\d)([.])(\d{1,3})(?=\s|$)/g;
  // Common case: the numbers are typed digits (not real footnote refs), so the
  // rich text already matches the plain text. Do a single global regex replace —
  // the includes()/replace() path below only hits the FIRST occurrence and a
  // dedup guard, so a number repeated in the same paragraph (e.g. ".22" twice)
  // would leave the later one un-superscripted.
  if (text === plainText) {
    return text.replace(FN_RE, (_m, punct, nums) => `${punct}<sup>${nums}</sup>`);
  }
  let result = text;
  let match: RegExpExecArray | null;
  while ((match = FN_RE.exec(plainText)) !== null) {
    const punct = match[1];
    const nums = match[2];
    const sup = punct + `<sup>${nums}</sup>`;
    const plainFragment = punct + nums;
    // Check if this fragment exists un-superscripted in the result
    if (result.includes(plainFragment) && !result.includes(sup)) {
      result = result.replace(plainFragment, sup);
    } else if (!result.includes(plainFragment) && !result.includes(sup)) {
      // Number was stripped from rich text entirely — find the punctuation
      // and append the superscript after it, at the right position
      const beforeInPlain = plainText.substring(0, match.index);
      // Find the corresponding punctuation in the result by counting
      // occurrences of the same trailing word+punct
      const trailingWord = plainText.substring(Math.max(0, match.index - 15), match.index + 1);
      const cleanTrailing = trailingWord.replace(/<[^>]+>/g, "");
      const posInResult = result.indexOf(cleanTrailing);
      if (posInResult >= 0) {
        const insertAt = posInResult + cleanTrailing.length;
        result = result.slice(0, insertAt) + `<sup>${nums}</sup>` + result.slice(insertAt);
      }
    }
  }
  return result;
}

// Re-anchor real Word footnotes inline at their reference offset. Each becomes a
// <fn>…</fn> wrapper (the renderer turns it into a float:footnote span, which
// WeasyPrint auto-numbers and places at the bottom of the correct page). Body
// text between markers still gets typed-citation superscripting; footnote text
// itself is left untouched by that pass.
function injectFootnotes(plain: string, fns: Array<{ offset: number; text: string }>): string {
  const sorted = [...fns].sort((a, b) => a.offset - b.offset);
  let result = "";
  let cursor = 0;
  for (const fn of sorted) {
    const off = Math.max(cursor, Math.min(fn.offset, plain.length));
    result += restoreFootnoteRefs(undefined, plain.slice(cursor, off));
    result += `<fn>${fn.text}</fn>`;
    cursor = off;
  }
  result += restoreFootnoteRefs(undefined, plain.slice(cursor));
  return result;
}

function restoreCitationSuperscripts(value: string): string {
  if (!value || value.includes("<sup>")) return value;
  return value
    .replace(/([.,:;])(\d{1,3}(?:,\d{1,3})*)(?=\s+[A-Z"'(""'']|[.\s]*$)/g,
      (_, punct, nums) => punct + nums.split(",").map((n: string) => `<sup>${n}</sup>`).join(","));
}

class SemanticExporter {
  async buildArticleJson(options: { skipSections?: boolean } = {}): Promise<ArticleJson> {
    const controls = await wordService.readSemanticControls();
    const outline = await wordService.readBodyOutline();
    const tables = await wordService.readTables();
    // PDF rendering consumes the ordered `blocks` stream. Its legacy `sections`
    // fallback is never reached when blocks exist, so avoid two extra full-body
    // Word passes on that path. XML/JATS/HTML callers keep the complete sections.
    const paragraphs = options.skipSections ? [] : await wordService.readParagraphs();
    const semanticParagraphs = options.skipSections ? [] : await wordService.readSemanticParagraphs();
    const authorDetails = await wordService.readAuthors();
    const figurePayloads = await wordService.readFigures();
    const references = await wordService.readReferences();
    const footnotes = await wordService.readFootnotes();
    const fnByPara = new Map<number, Array<{ offset: number; text: string }>>();
    for (const f of footnotes) {
      const arr = fnByPara.get(f.paraIndex) ?? [];
      arr.push({ offset: f.offset, text: f.text });
      fnByPara.set(f.paraIndex, arr);
    }
    const title = controls.find((item) => item.metadata.type === "title")?.text ?? "";
    const subtitle = controls.find((item) => item.metadata.type === "subtitle")?.text;
    const authors =
      authorDetails.length > 0
        ? authorDetails.map((author) => author.name.trim()).filter(Boolean)
        : controls.filter((item) => item.metadata.type === "author").map((item) => item.text);
    // A multi-paragraph abstract gets tagged as one control PER paragraph
    // (same pattern as references — see autoTagDocument's region==="abstract"
    // branch), not one control wrapping the whole thing. .find() here would
    // silently drop every paragraph after the first, so join every
    // abstract-tagged control's text in document order instead.
    let abstract = controls
      .filter((item) => item.metadata.type === "abstract")
      .map((item) => item.text)
      .join("\n\n");
    // Run-in abstracts embed a leading "Abstract" label in the same paragraph as
    // the body — either "Abstract:" (colon/period/dash) or "Abstract" alone on
    // its own line (a line break before the body). Strip that label so the panel
    // renders only the body under its own heading. Both branches require a real
    // separator, so a genuine abstract that merely starts with the word
    // "Abstract" (e.g. "Abstract algebra …") is never touched.
    abstract = abstract.replace(/^\s*abstract\s*[:.–—-]\s*/i, "").trim() || abstract;
    const firstBreak = abstract.search(/[\r\n\v\f\u2028\u2029]/);
    if (firstBreak >= 0 && /^\s*abstract\s*[:.–—-]?\s*$/i.test(abstract.slice(0, firstBreak))) {
      abstract = abstract.slice(firstBreak + 1).replace(/^\s+/, "").trim() || abstract;
    }
    // Strip a leading "Keywords:"/"Key words —" label the tagged control may
    // still carry (the author's own label text, not a real keyword) before
    // splitting into individual terms — mirrors the abstract run-in strip
    // above. Also drop any resulting term that is just the label itself.
    const keywordsRaw = (controls.find((item) => item.metadata.type === "keywords")?.text ?? "")
      .replace(/^\s*(?:key\s*words?|index\s+terms)\s*[:.–—-]\s*/i, "");
    const keywords = keywordsRaw
      .split(/[,;\n]/)
      .map((item) => item.trim())
      .filter(Boolean)
      .filter((item) => !/^(?:key\s*words?|index\s+terms)$/i.test(item.replace(/[:.\s]+$/, "")));

    const sections: ArticleSection[] = [];
    let currentSection: ArticleSection | null = null;

    if (!options.skipSections) {
      controls.forEach((item) => {
        if (item.metadata.type === "section") {
          if (RESERVED_FRONTMATTER_HEADINGS.has(item.text.trim().toLowerCase())) return;
          const section: ArticleSection = {
            id: item.metadata.id ?? crypto.randomUUID(),
            level: item.metadata.level ?? 1,
            title: item.text,
            paragraphs: [],
            children: []
          };
          if (section.level === 1 || sections.length === 0) {
            sections.push(section);
            currentSection = section;
          } else {
            const parent = findLastSectionAtLevel(sections, (section.level - 1) as 1 | 2) ?? sections[sections.length - 1];
            parent.children.push(section);
            currentSection = section;
          }
        }
        if (item.metadata.type === "paragraph" && currentSection) {
          currentSection.paragraphs.push(item.text);
        }
      });
    }

    const taggedText = new Set(semanticParagraphs.map((item) => item.trim()).filter(Boolean));
    const untaggedParagraphs = paragraphs.filter((paragraph) => !taggedText.has(paragraph));
    if (!options.skipSections && /^abstract$/i.test(abstract.trim())) {
      const abstractIndex = paragraphs.findIndex((paragraph) => /^abstract$/i.test(paragraph.trim()));
      abstract = paragraphs.slice(abstractIndex + 1).find((paragraph) => paragraph.trim().length > 30) ?? abstract;
    }
    if (!options.skipSections && untaggedParagraphs.length > 0) {
      const fallbackSection: ArticleSection =
        currentSection ??
        ({
          id: "untagged-body",
          level: 1,
          title: "Body",
          paragraphs: [],
          children: []
        } satisfies ArticleSection);
      if (!currentSection) sections.push(fallbackSection);
      fallbackSection.paragraphs.push(...untaggedParagraphs);
    }

    const figures = figurePayloads.map((item, index) => ({
      id: item.id,
      label: `Figure ${index + 1}`,
      caption: item.caption,
      imageBase64: item.imageBase64
    }));

    // One entry per REAL Word table, in document order. A table can carry more than
    // one table control (nested duplicates from re-running Auto-Tag), and
    // `contentControls` then lists every one of them; indexing `tables[index]` by
    // that inflated list mislabelled the tables ("Table 4/8/12") and left their rows
    // empty. The body outline names the control each table's cell paragraphs
    // actually sit in — take those, once each, in order.
    const allTableControls = controls.filter((item) => item.metadata.type === "table");
    const outlineTableIds: string[] = [];
    for (const entry of outline) {
      if (entry.type === "table" && entry.id && !outlineTableIds.includes(entry.id)) outlineTableIds.push(entry.id);
    }
    const semanticTables = outlineTableIds.length
      ? outlineTableIds
          .map((id) => allTableControls.find((item) => item.metadata.id === id))
          .filter((item): item is (typeof allTableControls)[number] => Boolean(item))
      : allTableControls;
    const articleTables = semanticTables.map((item, index) => ({
      id: item.metadata.id ?? `tbl${index + 1}`,
      label: `Table ${index + 1}`,
      caption: controls.find((caption) => caption.metadata.type === "table_caption" && caption.metadata.parent === item.metadata.id)?.text ?? "",
      notes: controls.filter((note) => note.metadata.type === "table_note" && note.metadata.parent === item.metadata.id).map((note) => note.text),
      rows: tables[index]?.rows ?? []
    }));

    const figureIds = new Set(figures.map((figure) => figure.id));
    const tableIds = new Set(articleTables.map((table) => table.id));
    const blocks: ArticleBlock[] = [];
    let referencesInserted = false;

    // Build the block stream by walking the body in document order. Untagged
    // paragraphs become body text in place (a proper PDF export must include
    // everything, tagged or not). Multi-paragraph controls (figure, table,
    // references) emit a single block, keyed by control id.
    const FRONTMATTER_TYPES = new Set(["title", "subtitle", "author", "doi", "abstract", "keywords", "footnote"]);
    const seenControls = new Set<number>();
    // Defends against a real Word bug: when a title/subtitle content control
    // doesn't wrap its whole paragraph (e.g. a stray run sits outside the
    // <w:sdt>), paragraph.parentContentControlOrNullObject comes back null,
    // so readBodyOutline reports the paragraph as untagged (type: null) even
    // though its text is the title/subtitle verbatim — duplicating it into
    // the body above the first heading. Strip all whitespace, not just
    // collapse it, per this pipeline's established text-matching pitfalls.
    const normalize = (s: string) => s.replace(/\s+/g, "").toLowerCase();
    const frontmatterEcho = new Set([title, subtitle].filter(Boolean).map((s) => normalize(s as string)));

    outline.forEach((entry, outlineIdx) => {
      const { type, controlId, text } = entry;

      if (type === null) {
        if (
          !entry.hasImage &&
          text &&
          !/^(?:https?:\/\/doi\.org\/)?10\.\d{4,9}\/\S+$/i.test(text.trim()) &&
          !frontmatterEcho.has(normalize(text)) &&
          !isReservedBodyLabel(text)
        ) {
          // Auto-tag: an untagged paragraph containing a native Word equation
          // object (Insert > Equation, OOXML <m:oMath>) is math, not body
          // text, even though the user never clicked the Equation button.
          // Read-time classification only — same pattern as the references
          // fallback above — never mutates the document.
          blocks.push(
            entry.hasEquation
              ? ({ type: "equation", text, _outlineIdx: outlineIdx } as any)
              : ({ type: "paragraph", text, list: entry.list, _outlineIdx: outlineIdx } as any)
          );
        }
        return;
      }
      if (FRONTMATTER_TYPES.has(type)) return;

      // A semantic `paragraph` control may intentionally wrap many real Word
      // paragraphs. Each one is a separate body block and must survive.
      // Deduplicate only container/object controls, which emit one block per
      // control; deduplicating paragraph controls dropped every paragraph
      // after the first (127 paragraphs in the real IEEE test document).
      if (type !== "paragraph" && controlId !== undefined) {
        if (seenControls.has(controlId)) return;
        seenControls.add(controlId);
      }

      if (type === "section") {
        // The labeled Abstract/Keywords/References blocks insert a heading control
        // titled "Libertatem <Label> Heading" (and tag it as a generic section).
        // Those are frontmatter — drop them from the body. A user-tagged body
        // heading literally named "Abstract" has a different control title and
        // must survive.
        if (entry.controlTitle && /^Libertatem (Abstract|Keywords|References) Heading$/i.test(entry.controlTitle)) return;
        if (isReservedBodyLabel(text)) return;
        blocks.push({ type: "section", id: entry.id ?? crypto.randomUUID(), level: (entry.level ?? 1) as 1 | 2 | 3 | 4 | 5 | 6, title: text });
      } else if (type === "paragraph") {
        if (text && !isReservedBodyLabel(text)) blocks.push({ type: "paragraph", text, list: entry.list, _outlineIdx: outlineIdx } as any);
      } else if (type === "blockquote") {
        if (text) blocks.push({ type: "blockquote", text, _outlineIdx: outlineIdx } as any);
      } else if (type === "equation") {
        if (text) blocks.push({ type: "equation", text, _outlineIdx: outlineIdx } as any);
      } else if (type === "figure") {
        if (entry.id && figureIds.has(entry.id)) blocks.push({ type: "figure", id: entry.id });
      } else if (type === "table") {
        if (entry.id && tableIds.has(entry.id)) blocks.push({ type: "table", id: entry.id });
      } else if (type === "reference" || type === "references") {
        if (!referencesInserted && references.length > 0) {
          blocks.push({ type: "references" });
          referencesInserted = true;
        }
      }
    });

    if (!referencesInserted && references.length > 0) {
      blocks.push({ type: "references" });
    }

    // Attach rich text to paragraph/blockquote blocks. Each was pushed with
    // its outline index stored in _outlineIdx so we can look up the correct
    // richText without re-walking with identical skip logic.
    const richBlocks = blocks.map((block) => {
      if (block.type !== "paragraph" && block.type !== "blockquote" && block.type !== "equation") return block;
      const idx = (block as any)._outlineIdx as number | undefined;
      const paraFns = idx !== undefined ? fnByPara.get(idx) : undefined;
      // Paragraphs with real footnotes are rebuilt from plain text with footnotes
      // re-anchored inline; otherwise keep the rich-text + typed-citation path.
      const final =
        paraFns && paraFns.length
          ? injectFootnotes(block.text, paraFns)
          : restoreFootnoteRefs(idx !== undefined ? outline[idx]?.richText : undefined, block.text);
      const clean = { ...block, text: final };
      delete (clean as any)._outlineIdx;
      return clean;
    });

    return {
      title,
      subtitle,
      authors,
      authorDetails: authorDetails.map((author) => ({
        name: author.name.trim(),
        affiliation: author.affiliation.trim(),
        email: author.email.trim(),
        orcid: author.orcid.trim(),
        ror: author.ror.trim(),
        ojsContributorId: author.ojsContributorId
      })),
      abstract,
      keywords,
      sections,
      blocks: richBlocks,
      figures,
      tables: articleTables,
      references,
      metadata: {
        generatedAt: new Date().toISOString(),
        source: "Libertatem Editor",
        schemaVersion: "1.0.0"
      }
    };
  }
}

function findLastSectionAtLevel(sections: ArticleSection[], level: 1 | 2): ArticleSection | null {
  const stack = [...sections];
  let found: ArticleSection | null = null;
  while (stack.length) {
    const section = stack.shift();
    if (!section) continue;
    if (section.level === level) found = section;
    stack.push(...section.children);
  }
  return found;
}

export const semanticExporter = new SemanticExporter();
